import React from 'react';
import { View } from 'react-native';
import { Feather } from '@expo/vector-icons';

import { normalizeRequiredFiles } from '../../../common/utils/people/documentRequiredFiles';
import { Text } from '../../components';
import { DateSelectorInput } from '../../components/input';
import { PrimaryButton, SecondaryButton } from '../../design/components';
import { useTheme } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { createMyDocumentsStyles } from './MyDocumentsView.styles';

// Dates and files the chosen document type asks for, with Cancel and Submit.
// `upload` is the state from useMyDocumentUpload.
const MyDocumentUploadForm = ({ upload, assignment = null, documentType, category = '' }) => {
	const styles = useThemedStyles(createMyDocumentsStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const {
		pendingAssetsByKey,
		issuedAt,
		setIssuedAt,
		expiresAt,
		setExpiresAt,
		isSubmitting,
		addPhoto,
		addFile,
		cancelUpload,
		submitUpload,
	} = upload;

	const settings = documentType?.settings || {};
	const requiresIssueDate = Boolean(settings.requiresIssueDate);
	const requiresExpiryDate = Boolean(settings.requiresExpiryDate);
	const requiredFiles = normalizeRequiredFiles(settings);
	const hasAllRequiredFiles = requiredFiles
		.filter(requiredFile => requiredFile.required)
		.every(requiredFile => pendingAssetsByKey[requiredFile.key]);
	const hasDocumentName = Boolean(documentType?.id || String(category).trim());
	const canSubmit = Boolean(hasDocumentName && hasAllRequiredFiles &&
		(!requiresIssueDate || issuedAt) &&
		(!requiresExpiryDate || expiresAt));

	return (
		<View style={styles.uploadForm}>
			{
				requiresIssueDate && (
					<View style={styles.formField}>
						<Text style={styles.formLabel}>Issue date</Text>
						<DateSelectorInput value={issuedAt} onChange={setIssuedAt} />
					</View>
				)
			}
			{
				requiresExpiryDate && (
					<View style={styles.formField}>
						<Text style={styles.formLabel}>Expiry date</Text>
						<DateSelectorInput value={expiresAt} onChange={setExpiresAt} />
					</View>
				)
			}
			{requiredFiles.map((requiredFile) => {
				const pendingAsset = pendingAssetsByKey[requiredFile.key];
				const label = requiredFile.label || 'File';

				return (
					<View key={requiredFile.key} style={styles.formField}>
						<Text style={styles.formLabel}>
							{label}
							{requiredFile.required ? '' : ' (optional)'}
						</Text>
						{
							pendingAsset ? (
								<View style={styles.fileNameRow}>
									<Feather name="paperclip" size={16} color={colors.iconMuted} />
									<Text style={styles.fileName}>{pendingAsset.fileName || pendingAsset.name || 'File selected'}</Text>
								</View>
							) : null
						}
						<View style={styles.fileButtons}>
							<SecondaryButton
								label={pendingAsset ? 'Replace with photo' : 'Add photo'}
								icon="camera"
								onPress={() => addPhoto(requiredFile.key)}
								disabled={isSubmitting}
							/>
							<SecondaryButton
								label={pendingAsset ? 'Replace with file' : 'Choose file'}
								icon="file"
								onPress={() => addFile(requiredFile.key)}
								disabled={isSubmitting}
							/>
						</View>
					</View>
				);
			})}
			<Text style={styles.fileGuidance}>
				PDF, Word document or image. Maximum 10 MB.
			</Text>
			<View style={styles.formButtons}>
				<SecondaryButton
					label="Cancel"
					onPress={cancelUpload}
					disabled={isSubmitting}
					style={styles.formButton}
				/>
				<PrimaryButton
					label={isSubmitting ? 'Uploading…' : 'Submit document'}
					onPress={() => submitUpload({ assignment, documentType, category }, requiredFiles)}
					disabled={isSubmitting || !canSubmit}
					size="compact"
					style={styles.formButton}
				/>
			</View>
		</View>
	);
};

export default MyDocumentUploadForm;
